
import { BaseNotification } from '@/types/notifications';
import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Extend the ServiceWorkerRegistration interface to include sync
declare global {
  interface ServiceWorkerRegistration {
    sync?: {
      register(tag: string): Promise<void>;
    };
  }
}

interface OfflineNotificationDB extends DBSchema {
  pendingNotifications: {
    key: string;
    value: {
      notification: BaseNotification;
      timestamp: number;
      attempts: number;
      id: string;
    };
  };
}

class OfflineNotificationService {
  private db: IDBPDatabase<OfflineNotificationDB> | null = null;

  async initialize() {
    if (!this.db) {
      this.db = await openDB<OfflineNotificationDB>('offline-notifications', 1, {
        upgrade(db) {
          db.createObjectStore('pendingNotifications', { keyPath: 'id' });
        },
      });
    }
  }

  async queueNotification(notification: BaseNotification) {
    await this.initialize();
    if (!this.db) return;

    // Ensure notification has an id
    if (!notification.id) {
      notification.id = `notification_${Date.now()}`;
    }

    const id = notification.id;
    await this.db.add('pendingNotifications', {
      notification,
      timestamp: Date.now(),
      attempts: 0,
      id
    });

    // Register background sync if available
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const swRegistration = await navigator.serviceWorker.ready;
        if (swRegistration.sync) {
          await swRegistration.sync.register('sync-notifications');
        } else {
          console.warn('Background sync is not available in this browser');
        }
      } catch (err) {
        console.error('Background sync registration failed:', err);
      }
    }
  }

  async getPendingNotifications(): Promise<BaseNotification[]> {
    await this.initialize();
    if (!this.db) return [];

    const pending = await this.db.getAll('pendingNotifications');
    return pending.map(p => p.notification);
  }

  async removeNotification(id: string) {
    await this.initialize();
    if (!this.db) return;

    await this.db.delete('pendingNotifications', id);
  }
}

export const offlineNotificationService = new OfflineNotificationService();
